'use client'

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Play } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { NINXL_HERO_DEMO_SRC } from '@/lib/landing-demos'

export {
  NIN_HERO_DEMO_SRC,
  NINK_HERO_DEMO_SRC,
  NINX_HERO_DEMO_SRC,
  NINXL_HERO_DEMO_SRC,
} from '@/lib/landing-demos'

/** Floating play control + minimal demo video dialog */
export const NinxlHeroDemoPlayer = memo(function NinxlHeroDemoPlayer({
  className,
  variant = 'default',
  demoSrc = NINXL_HERO_DEMO_SRC,
  posterSrc,
  ariaLabel = 'Play NINX hero demo',
  dialogTitle = 'NINX hero demo',
  triggerLabel,
}: {
  className?: string
  variant?: 'default' | '8bit'
  demoSrc?: string
  /** baby: Optional poster frame shown before the video starts */
  posterSrc?: string
  ariaLabel?: string
  dialogTitle?: string
  /** baby: Hover/visible label under the play icon */
  triggerLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  // baby: 8bit keeps "Play demo"; default hover chip says "Watch demo"
  const resolvedTriggerLabel =
    triggerLabel ?? (variant === '8bit' ? 'Play demo' : 'Watch demo')

  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      videoRef.current?.pause()
    }
  }, [])

  // baby: Start playback when the dialog opens
  useEffect(() => {
    if (!open) return
    const video = videoRef.current
    if (!video) return
    void video.play().catch(() => undefined)
  }, [open])

  return (
    <>
      <button
        type="button"
        className={cn(
          'ninxl-hero-demo-play group absolute left-1/2 top-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center',
          variant === '8bit' && 'ninxl-hero-demo-play-8bit',
          className,
        )}
        onClick={handleOpen}
        aria-label={ariaLabel}
      >
        {variant === '8bit' ? (
          <span className="ninxl-hero-demo-play-8bit-btn relative flex flex-col items-center gap-2 px-6 py-4 transition group-hover:translate-x-[2px] group-hover:translate-y-[2px]">
            <span className="flex h-10 w-10 items-center justify-center border-2">
              <Play className="h-5 w-5 fill-white text-white" aria-hidden />
            </span>
            <span className="font-press-start-2p text-[8px] uppercase tracking-wider md:text-[10px]">
              {resolvedTriggerLabel}
            </span>
          </span>
        ) : (
          <>
            <span className="ninxl-hero-demo-play-ring absolute inset-0 rounded-full" aria-hidden />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[color:var(--ninxl-brand-teal)]/25 bg-white/95 text-[color:var(--ninxl-brand-teal)] shadow-[0_16px_40px_-12px_rgba(33,114,141,0.45)] backdrop-blur transition duration-300 group-hover:scale-105 group-hover:border-[color:var(--ninxl-brand-teal)]/40 group-hover:shadow-[0_20px_48px_-10px_rgba(33,114,141,0.5)] md:h-[4.5rem] md:w-[4.5rem]">
              <Play className="ml-0.5 h-7 w-7 fill-current" aria-hidden />
            </span>
            <span className="ninxl-hero-demo-play-label absolute -bottom-9 whitespace-nowrap rounded-full border border-[color:var(--ninxl-brand-muted)]/25 bg-white/92 px-3 py-1 text-[10px] font-semibold uppercase tracking-[1.5px] text-[color:var(--ninxl-brand-teal)] shadow-sm opacity-0 transition duration-300 group-hover:opacity-100">
              {resolvedTriggerLabel}
            </span>
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            'max-w-4xl gap-0 overflow-hidden border-0 bg-black p-0 sm:rounded-xl',
            variant === '8bit' &&
              'ninxl-hero-demo-dialog-8bit border-[3px] border-[color:var(--ninxl-8bit-border)] shadow-[6px_6px_0_var(--ninxl-8bit-dim)]'
          )}
        >
          <DialogTitle className="sr-only">{dialogTitle}</DialogTitle>
          <video
            ref={videoRef}
            className="ninxl-hero-demo-video aspect-video w-full bg-black object-contain"
            src={demoSrc}
            poster={posterSrc}
            controls
            playsInline
            preload="metadata"
          />
        </DialogContent>
      </Dialog>
    </>
  )
})